import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { useFormik } from 'formik';
import Breadcrumb from '../../../components/Shared/Breadcrumb';
import { Card, CardBody } from '../../../components/card/card';
import { Form } from 'react-bootstrap';
import RoutesList from './RoutesList';
import RouteJourneyPlanList from './RouteJourneyPlanList';
import {
  GetEquipmentsRoutesList,
  GetListLocations,
  InsertEquipmentsRoutes
} from '../../../store/FLEET/EquipmentsRoutes/equipmentsRoutesSlice';
import CustomAlert from '../../../components/Shared/Alert/CustomAlert';
import { EquipmentRoutesSchema } from '../../ValidationForm/validationSchema';
import Swal from 'sweetalert2';
import Loading from '@components/Shared/Loader/Loading';
import { usePermissions } from '@Helper/Permisstions/usePermissions';

const AddEquipmentRoutes = () => {
  const { HasPermission } = usePermissions();
  const { Id } = useParams();
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const Navigate = useNavigate();
  const loading = useAppSelector(state => state.EquipmentsRoutes.loading);
  const [JourneyPlanList, setJourneyPlanList] = useState<any[]>([]);

  useEffect(() => {
    dispatch(GetListLocations());
  }, []);

  const formik = useFormik({
    initialValues: {
      fleetEquipmentsId: '',
      name: '',
      name2: '',
      startTime: '',
      endTime: '',
      description: '',
      routeJourneyPlanList: {
        fleetEquipmentsRouteId: '',
        referenceId: '',
        referenceType: '',
        sequence: ''
      }
    },
    validationSchema: EquipmentRoutesSchema(t),
    onSubmit: values => {
      if (JourneyPlanList?.length < 2) {
        Swal.fire('يجب اختيار');
        formik.setSubmitting(false);
        return;
      }

      dispatch(
        InsertEquipmentsRoutes({
          fleetEquipmentsId: Id,
          name: values.name,
          name2: values.name2,
          startTime: values.startTime,
          endTime: values.endTime,
          description: values.description,
          routeJourneyPlanList: JourneyPlanList?.map(item => ({
            fleetEquipmentsRouteId: item.fleetEquipmentsRouteId,
            referenceId: item.referenceId,
            referenceType: item.referenceType,
            sequence: item.sequence
          }))
        })
      )
        .unwrap()
        .then(res => {
          if (res?.succeeded === true) {
            CustomAlert({ action: 'Add' });
            dispatch(GetEquipmentsRoutesList());
            formik.resetForm();
          } else {
            formik.setSubmitting(false);
            CustomAlert({ action: 'Error', msg: res?.message });
          }
        })
        .catch((error: any) => {
          formik.setSubmitting(false);
          CustomAlert({ action: 'Error' });
        });
    }
  });

  //____________________________

  //#region
  const location = useLocation();
  const [referenceName, setReferenceName] = useState(null); // State to hold ReferenceName
  useEffect(() => {
    if (location.state && location.state.ReferenceName) {
      const { ReferenceName } = location.state;
      if (ReferenceName) {
        setReferenceName(ReferenceName); // Update ReferenceName state
      }
    }
  }, []);
  //#endregion
  //____________________________

  return (
    <div>
      <Breadcrumb
        BreadcrumbList={[
          {
            name: 'Home',
           link: ':moduleHome'
          },
          {
            name: 'Equipment',
            link: '/FLEET/Equipments'
          },
          {
            name: 'Journeys',
            link: null
          }
        ]}
        PageName='Journeys'
        subtitle={referenceName}
      />

      <Card>
        <CardBody>
          <Loading loading={loading} children={<></>} />
          <Form onSubmit={formik.handleSubmit}>
            <div className='row'>
              <div className='col-xl-4'>
                <div className='form-group mb-3'>
                  <label className='form-label'>{t('LocalName')}</label>
                  <Form.Control
                    className='form-control'
                    type='text'
                    autoComplete='off'
                    name='name2'
                    onChange={formik.handleChange}
                    value={formik.values.name2}
                    isInvalid={!!(formik.touched.name2 && formik.errors.name2)}
                  />
                  <Form.Control.Feedback type='invalid'>{formik.errors.name2}</Form.Control.Feedback>
                </div>
              </div>

              <div className='col-xl-4'>
                <div className='form-group mb-3'>
                  <label className='form-label'>{t('GlobalName')}</label>
                  <Form.Control
                    className='form-control'
                    type='text'
                    autoComplete='off'
                    name='name'
                    onChange={formik.handleChange}
                    value={formik.values.name}
                    isInvalid={!!(formik.touched.name && formik.errors.name)}
                  />
                  <Form.Control.Feedback type='invalid'>{formik.errors.name}</Form.Control.Feedback>
                </div>
              </div>

              <div className='col-xl-2'>
                <div className='form-group mb-3'>
                  <label className='form-label'>{t('StartTime')}</label>
                  <Form.Control
                    className='form-control'
                    type='time'
                    name='startTime'
                    onChange={formik.handleChange}
                    value={formik.values.startTime}
                    isInvalid={!!(formik.touched.startTime && formik.errors.startTime)}
                  />
                  <Form.Control.Feedback type='invalid'>{formik.errors.startTime}</Form.Control.Feedback>
                </div>
              </div>

              <div className='col-xl-2'>
                <div className='form-group mb-3'>
                  <label className='form-label'>{t('EndTime')}</label>
                  <Form.Control
                    className='form-control'
                    type='time'
                    name='endTime'
                    onChange={formik.handleChange}
                    value={formik.values.endTime}
                    isInvalid={!!(formik.touched.endTime && formik.errors.endTime)}
                  />
                  <Form.Control.Feedback type='invalid'>{formik.errors.endTime}</Form.Control.Feedback>
                </div>
              </div>
            </div>

            <div className='row'>
              <div className='col-xl-6'>
                <div className='form-group mb-3'>
                  <label className='form-label'>{t('Note')}</label>
                  <Form.Control
                    className='form-control'
                    as='textarea'
                    rows={2}
                    name='description'
                    onChange={formik.handleChange}
                    value={formik.values.description}
                  />
                </div>
              </div>

              <div className='col-xl-6'>
                <RouteJourneyPlanList JourneyPlanList={JourneyPlanList} setJourneyPlanList={setJourneyPlanList} />
              </div>
            </div>

            <div className='text-center mt-2'>
              <button
                hidden={!HasPermission('FleetAddRouteEquipment')}
                disabled={formik.isSubmitting}
                type='submit'
                className='btn me-1 btn-theme mb-1 '
              >
                {formik.isSubmitting ? (
                  <div className='spinner-border spinner-border-sm me-2'></div>
                ) : (
                  <i className='fas fa-lg fa-fw me-2 fa-save'></i>
                )}
                {t('Save')}
              </button>
              <button
                type='button'
                className=' btn me-1 btn-default mb-1'
                onClick={() => Navigate('/FLEET/Equipments', { replace: true })}
              >
                <i className='fa fa-power-off fa-fw me-1'></i> {t('Cancel')}
              </button>
            </div>
          </Form>
          <hr />
          <div className=' bg-inverse bg-opacity-5 rounded-4 pt-1'>
            <RoutesList />
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default AddEquipmentRoutes;
